import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductHeroAnimation } from "./ProductHeroAnimation";
import { useLanguage } from "@/hooks/useLanguage";

interface ProductHeroProps {
  productKey: string;
  icon: string;
  accentClass?: string;
}

export function ProductHero({ productKey, icon, accentClass = "bg-petrol-light" }: ProductHeroProps) {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const p = `productPages.${productKey}`;

  return (
    <section className="relative bg-primary text-primary-foreground py-12 lg:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />

      {/* Subtle decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-white/[0.02]" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/[0.03]" />
      </div>

      <div className="relative container mx-auto px-4 lg:px-8">
        <Link
          to={getLocalizedPath("/tjanster")}
          className="inline-flex items-center text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors mb-8"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          {t("productPages.backToProducts")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-3">
              {t(`${p}.hero.tagline`)}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              {t(`${p}.hero.title`)}
            </h1>
            <p className="mt-6 text-base md:text-lg text-primary-foreground/70 font-body leading-relaxed max-w-lg">
              {t(`${p}.overview`)}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                asChild
                className="bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
              >
                <Link to={getLocalizedPath("/bestall")}>
                  {t("productPages.ctaOrder")}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                asChild
                className="bg-white/15 border border-white/30 text-primary-foreground hover:bg-white/25 font-medium text-base backdrop-blur-sm"
              >
                <Link to={getLocalizedPath("/kontakt?type=demo")}>{t("productPages.ctaButton")}</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center"
          >
            <ProductHeroAnimation productKey={productKey} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
